/**
 * Relative-date helpers — single source of truth for the project-track
 * sections (Artifact gallery / Memory / Tasks / Automations).
 *
 * Phase 4.2 + Lingui pass (June 13, 2026): the four sections each
 * carried a slightly-different copy of these helpers, hard-coding
 * Italian anchors (``oggi`` / ``ieri`` / ``domani``). Code review
 * D1 / C4 flagged it: the per-section drift made the UI inconsistent
 * and the literals broke ``lingui extract``. This module now centralises
 * BOTH the calendar math AND the locale strings.
 *
 * Localisation contract:
 *
 *   - The pure shape ``formatRelativeDate(iso, options)`` keeps the
 *     italian-anchor defaults so existing call sites continue to work
 *     untouched (zero regression for the current Italian-only product).
 *   - The Lingui-aware variant ``formatRelativeDateI18n(iso, i18n,
 *     options)`` resolves the anchor labels via ``msg`` so the strings
 *     show up in the catalog and EN/other locales get the right words.
 *
 * Callers that already have ``useLingui()`` mounted (every section in
 * the project track) should prefer the i18n variant. Callers running
 * outside a Lingui-aware tree (e.g. tests, server-side previews) keep
 * the pure function with italian defaults.
 */

import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";

export interface RelativeDateOptions {
  /** When true, include ``"HH:MM"`` after the day label (today/tomorrow/date). */
  readonly withTime?: boolean;
  /**
   * When true, recognise yesterday → ``"ieri"`` (default behaviour for
   * Memory/Artifact past-facing surfaces). Setting it to false keeps the
   * helper forward-facing (Tasks/Automations want ``"domani"`` only).
   */
  readonly recogniseYesterday?: boolean;
  /**
   * When true, recognise tomorrow → ``"domani"`` (Tasks / Automations).
   * Default true; turn it off on past-facing surfaces (Memory / Artifact)
   * where "domani" can't happen anyway.
   */
  readonly recogniseTomorrow?: boolean;
}

const DEFAULT_OPTIONS: Required<RelativeDateOptions> = {
  withTime: false,
  recogniseYesterday: true,
  recogniseTomorrow: true,
};

/**
 * Anchor label set — one row per recognised relative day. Centralising
 * the keys means a future locale (EN, ES) only needs to translate three
 * strings to localise every consumer of this helper.
 */
export interface RelativeDateLabels {
  readonly today: string;
  readonly yesterday: string;
  readonly tomorrow: string;
}

/** Italian defaults — keep the legacy behaviour for pure-function callers. */
export const ITALIAN_DATE_LABELS: RelativeDateLabels = {
  today: "oggi",
  yesterday: "ieri",
  tomorrow: "domani",
};

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function sameYMD(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Internal core — the shared math. Both ``formatRelativeDate`` and
 * ``formatRelativeDateI18n`` reduce to this with their respective
 * anchor labels.
 */
function formatRelativeCore(
  iso: string | null | undefined,
  labels: RelativeDateLabels,
  options?: RelativeDateOptions,
): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const d = new Date(t);
  const now = new Date();
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  const time = opts.withTime ? ` ${hh}:${mm}` : "";

  if (sameYMD(d, now)) return `${labels.today}${time}`;

  if (opts.recogniseYesterday) {
    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    if (sameYMD(d, y)) return `${labels.yesterday}${time}`;
  }
  if (opts.recogniseTomorrow) {
    const tm = new Date(now);
    tm.setDate(now.getDate() + 1);
    if (sameYMD(d, tm)) return `${labels.tomorrow}${time}`;
  }

  const dd = pad2(d.getDate());
  const mo = pad2(d.getMonth() + 1);
  if (d.getFullYear() !== now.getFullYear()) {
    // Cross-year — omit the time; pin the year to disambiguate ``15/06``.
    return `${dd}/${mo}/${d.getFullYear()}`;
  }
  return `${dd}/${mo}${time}`;
}

/**
 * Pure / italian-defaults variant. Returns the date with hard-coded
 * Italian anchor labels — kept for backwards compatibility and for any
 * caller outside a Lingui-aware tree (tests, SSR previews, …).
 */
export function formatRelativeDate(
  iso: string | null | undefined,
  options?: RelativeDateOptions,
): string {
  return formatRelativeCore(iso, ITALIAN_DATE_LABELS, options);
}

/**
 * Lingui-aware variant — resolves anchor labels via ``msg`` so they end
 * up in the extracted catalog and translate alongside every other
 * string. The shape is identical to ``formatRelativeDate``; only the
 * ``oggi`` / ``ieri`` / ``domani`` words swap to the active locale's.
 *
 * Pass the ``i18n`` object from ``useLingui()`` — keeping the resolver
 * dependency explicit (instead of importing the singleton) makes the
 * helper trivially testable and avoids the "stale-locale on first
 * render" pitfall the project's Lingui setup warns about.
 */
export function formatRelativeDateI18n(
  iso: string | null | undefined,
  i18n: I18n,
  options?: RelativeDateOptions,
): string {
  return formatRelativeCore(
    iso,
    {
      today: i18n._(msg`oggi`),
      yesterday: i18n._(msg`ieri`),
      tomorrow: i18n._(msg`domani`),
    },
    options,
  );
}
